import i18n, {
  type Resource,
  type ResourceKey,
  type ResourceLanguage,
  type TOptions,
} from 'i18next';
import { useCallback } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';

import { InternalServerError } from '../error';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { data } from './translations';

export { i18n };

export function getResources(
  translationData: {
    [key: string]: {
      [key: string]: {
        [key: string]: string;
      };
    };
  },
  translations: Resource
): Resource {
  // need to convert new to old
  // iterate namespaces (file structure / global)
  const namespaces = Object.keys(translationData);
  for (let i = 0; i < namespaces.length; i += 1) {
    const ns = namespaces[i];
    const nsObj = translationData[ns];
    if (nsObj == null) {
      continue;
    }

    // iterate keys
    const keys = Object.keys(nsObj);
    for (let c = 0; c < keys.length; c += 1) {
      const key = keys[c];
      const keyObj = nsObj[key];
      if (keyObj == null) {
        continue;
      }

      // iterate locales
      const locales = Object.keys(keyObj);
      for (let d = 0; d < locales.length; d += 1) {
        const locale = locales[d];
        const value = keyObj[locale];

        // cache the value key
        const tLocale: ResourceLanguage = translations[locale] ?? { translation: {} };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const tNs: ResourceKey = (tLocale.translation as any)[ns] ?? {};
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (tNs as any)[key] = value;

        // recache the object
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (tLocale.translation as any)[ns] = tNs;
        translations[locale] = tLocale;
      }
    }
  }

  return translations;
}

const resources = getResources(data, { en: { translation: data } });

export function init() {
  return i18n.use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources,
  });
}

export function changeLanguage(language: string) {
  return i18n.changeLanguage(language);
}

export function isInit() {
  return i18n.isInitialized;
}

export function t(key: string, options?: TOptions, ignoreMissing?: boolean) {
  const result = i18n.t(key, options);
  if (result === key && !ignoreMissing) {
    captureException(
      new InternalServerError(`key not found: ${key}`),
      {
        file: 'services/translation.ts',
        method: 't',
        infoMessage: `key not found: ${key}`,
        key,
      },
      trackError
    );
  }

  return result;
}

export function tNamespace(namespace: string) {
  return (key: string, options?: TOptions, ignoreMissing?: boolean) => {
    const namespacedKey = `${namespace}.${key}`;
    return t(namespacedKey, options, ignoreMissing);
  };
}

export function tGlobal(key: string, options?: TOptions) {
  return t(`global.${key}`, options);
}

export function useTNamespace(namespace: string) {
  const { i18n } = useTranslation();
  return useCallback(
    function (key: string, options?: TOptions, ignoreMissing?: boolean) {
      return t(`${namespace}.${key}`, options, ignoreMissing);
    },
    [namespace, i18n.language]
  );
}
